import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PaymentCard from "../components/payments/PaymentCard";
import PageError from "../components/common/PageError";
import DateRangeFilter from "../components/common/DateRangeFilter";

import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getPayments } from "../redux/actions/paymentActions";

const PaymentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const today = new Date().toISOString().split("T")[0];

  const {
    items: payments,
    pagination,
    loading,
    error,
  } = useAppSelector((state) => state.payment);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [page, setPage] = useState<number>(1);
  const limit = 12;

  const fetchPaymentsList = useCallback((): void => {
    dispatch(
      getPayments({
        page,
        size: limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, startDate, endDate]);

  useEffect(() => {
    fetchPaymentsList();
  }, [fetchPaymentsList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  const renderContent = (): React.JSX.Element => {
    if (loading) return <AppCircularProgress />;
    if (error) return <PageError error={error} onRetry={fetchPaymentsList} />;

    if (!payments || payments.length === 0) {
      return (
        <Typography sx={{ mt: 5, textAlign: "center" }} color="textSecondary">
          No payments found.
        </Typography>
      );
    }

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {payments.map((payment) => (
            <PaymentCard key={payment.id} payment={payment} />
          ))}
        </Box>

        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Payments History"
      subtitle="View all incoming and outgoing payments."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search payments..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>
      {renderContent()}
    </PageContainer>
  );
};

export default PaymentsPage;
